import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  FileInput,
  Group,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useNavigate } from 'react-router';
import { BOS_EGITIM_SUZGECI, EGITIM_SAYFA_BOYUTU, etkinEgitimSuzgeciSayisi } from '@isg/validation';
import type { EgitimSuzgeci } from '@isg/validation';
import {
  useCiktiIndir,
  useEgitimOptions,
  useEgitimYukle,
  useEgitimler,
  useEksikTemelIsg,
  useKapilar,
} from '../api/queries';
import { EGITIM_YONTEM_ETIKET } from '../i18n/tr';
import { Sayfalama, SuzgecGostergesi, KapiSebebi, Rozet } from '../sozlesme';
import { BosListeNotu, QueryStatus } from './parts';
import { ANLAM } from '../theme';
import { gunGirdisiIso, tarihGoster } from '../lib/format';
import { EgitimDevirYuklemePaneli, YuklemeSonucTablosu } from './egitim-devir-yukleme';

/**
 * Eğitim listesi (F3B·S7 — Kapsam §16.10; ✏️ tur 5 / Y1).
 *
 * **Y1 — SÜZGEÇ SUNUCUDA, SAYFALAMA + TOPLAM** (ekipman Y3 ikizi; P4).
 * İstemci sayfalaması ve istemci süzgeci KALKTI: bahçenin tamamını tarayıcıya
 * indirmek, süzgeci "yüklendikten sonra" çalışan bir süse çeviriyordu ve
 * süzgeç seçenekleri yalnız yüklenen kayıtlardan türüyordu. Süzgeç dizgisi
 * `egitimSuzgecSorgusu`dan (tek fonksiyon).
 *
 * İki türetilmiş gösterge SUNUCUDAN gelir, ekranda YENİDEN HESAPLANMAZ:
 * değerlendirme oranı (T4) ve **süre uyarısı** rozeti (EK.1 — UYARI, ENGEL
 * DEĞİL). Üstteki *"Yeni eğitim kaydı"* düğmesi E1 kuralının karşılığıdır —
 * eğitim BAĞIMSIZ kayıttır (P1), ayrı sayfada açılır.
 */
export function EgitimlerScreen(): React.JSX.Element {
  const [suzgec, setSuzgec] = useState<EgitimSuzgeci>(BOS_EGITIM_SUZGECI);
  const egitimler = useEgitimler(suzgec);
  const options = useEgitimOptions();
  // P13-b (madde 225): toplu yükleme kapısı SUNUCUDAN (Y4 — yalnız holding yön.).
  const kapilar = useKapilar();
  const holdingYoneticisi = kapilar.data?.egitimYuklemeKapisi.yazabilir ?? false;
  const navigate = useNavigate();
  const [yuklemeAcik, setYuklemeAcik] = useState(false);
  // SÖ-1 — sistem öncesi (devir) yüklemesi: AYNI kapı, ayrı panel (iki adım).
  const [devirAcik, setDevirAcik] = useState(false);
  // TEMEL İSG "kaydı yok" süzgeci (F4·S1 — §22.2/9 tip 4'ün detay yüzeyi):
  // eğitim listesi OLMAYAN kaydı gösteremez, bu yüzden ayrı bir yokluk
  // sorgusudur. Küme, günlük özetin saydığı kümenin ta kendisidir.
  const [temelIsg, setTemelIsg] = useState<string | null>(null);
  const eksikModu = temelIsg === 'eksik';
  const eksikler = useEksikTemelIsg(eksikModu);

  const satirlar = egitimler.data?.satirlar ?? [];
  const toplam = egitimler.data?.toplam ?? 0;
  const etkin = etkinEgitimSuzgeciSayisi(suzgec);

  /** Süzgeç değişince sayfa 1'e döner — yoksa boş sayfada kalınır. */
  function suzgeciDegistir(yama: Partial<EgitimSuzgeci>): void {
    setSuzgec((eski) => ({ ...eski, ...yama, sayfa: 1 }));
  }

  return (
    <Stack>
      <Group justify="space-between" align="flex-end">
        <Title order={3}>Eğitimler</Title>
        <Group align="flex-end">
          {/* Yetki SUNUCUDAN (`kayitAcabilir` = egitim_kaydi listesi üyeliği). */}
          {options.data?.kayitAcabilir && (
            <Button data-testid="egitim-ac" onClick={() => void navigate('/egitimler/yeni')}>
              Yeni eğitim kaydı
            </Button>
          )}
          {holdingYoneticisi && (
            <Button
              variant="default"
              data-testid="egitim-yukleme-ac"
              onClick={() => {
                setYuklemeAcik((acik) => !acik);
                setDevirAcik(false);
              }}
            >
              {yuklemeAcik ? 'Yükleme panelini kapat' : 'Kayıtları toplu yükle'}
            </Button>
          )}
          {holdingYoneticisi && (
            <Button
              variant="default"
              data-testid="egitim-devir-yukleme-ac"
              onClick={() => {
                setDevirAcik((acik) => !acik);
                setYuklemeAcik(false);
              }}
            >
              {devirAcik ? 'Sistem öncesi panelini kapat' : 'Sistem öncesi kayıtları yükle'}
            </Button>
          )}
          {kapilar.data && (
            <KapiSebebi
              durum={kapilar.data.egitimYuklemeKapisi}
              nesne="Kayıtları toplu"
              fiil="yükler"
              testId="egitim-yukleme-yazamama-sebebi"
            />
          )}
        </Group>
      </Group>
      <Group align="flex-end">
        <Select
          label="Eğitim türü"
          placeholder="Tümü"
          clearable
          searchable
          w={240}
          data-testid="egitim-suzgec-tur"
          data={(options.data?.egitimTurleri ?? []).map((t) => ({ value: t.id, label: t.ad }))}
          value={suzgec.egitimTuruRefId}
          onChange={(deger) => suzgeciDegistir({ egitimTuruRefId: deger })}
        />
        <Select
          label="İşyeri"
          placeholder="Tümü"
          clearable
          searchable
          w={220}
          data-testid="egitim-suzgec-isyeri"
          data={(options.data?.workplaces ?? []).map((w) => ({ value: w.id, label: w.ad }))}
          value={suzgec.workplaceId}
          onChange={(deger) => suzgeciDegistir({ workplaceId: deger })}
        />
        {/* C2 (5½) — takvim seçici; yalnız geçerli gün süzgece iner
            (`gunGirdisiIso`, madde 137 deseni). */}
        <TextInput
          label="Başlangıç"
          type="date"
          w={160}
          data-testid="egitim-suzgec-baslangic"
          value={suzgec.baslangic ?? ''}
          onChange={(e) => {
            const deger = e.currentTarget.value;
            suzgeciDegistir({ baslangic: gunGirdisiIso(deger) === null ? null : deger });
          }}
        />
        <TextInput
          label="Bitiş"
          type="date"
          w={160}
          data-testid="egitim-suzgec-bitis"
          value={suzgec.bitis ?? ''}
          onChange={(e) => {
            const deger = e.currentTarget.value;
            suzgeciDegistir({ bitis: gunGirdisiIso(deger) === null ? null : deger });
          }}
        />
        {/* TÜRETİLMİŞ süzgeç — sunucu satırları çözdükten SONRA süzer,
            toplam da ondan sonra sayılır. */}
        <Switch
          label="Yalnız süre uyarılı"
          data-testid="egitim-suzgec-uyari"
          checked={suzgec.sureUyarisi}
          onChange={(olay) => suzgeciDegistir({ sureUyarisi: olay.currentTarget.checked })}
          mb={8}
        />
        <Select
          label="Temel İSG"
          placeholder="—"
          clearable
          w={260}
          data-testid="egitim-suzgec-temel-isg"
          data={[{ value: 'eksik', label: 'Kaydı yok (temel İSG eğitimi girilmemiş)' }]}
          value={temelIsg}
          onChange={setTemelIsg}
        />
      </Group>
      {holdingYoneticisi && yuklemeAcik && <EgitimYuklemePaneli />}
      {holdingYoneticisi && devirAcik && <EgitimDevirYuklemePaneli options={options.data} />}
      {eksikModu ? (
        <>
          <QueryStatus queries={[eksikler]} />
          {eksikler.isSuccess && eksikler.data.length === 0 && (
            <BosListeNotu mesaj="Aktif istihdamlı herkesin temel İSG eğitimi kayıtlı." />
          )}
          {(eksikler.data?.length ?? 0) > 0 && (
            <Table striped data-testid="egitim-eksik-listesi">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Kişi</Table.Th>
                  <Table.Th>İşveren</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(eksikler.data ?? []).map((satir) => (
                  <Table.Tr key={satir.personId} data-testid={`egitim-eksik-${satir.personId}`}>
                    <Table.Td>{satir.adSoyad}</Table.Td>
                    <Table.Td>{satir.isverenAd}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </>
      ) : (
        <>
          <QueryStatus queries={[egitimler, options]} />
          <SuzgecGostergesi
            etkinSayisi={etkin}
            onTemizle={() => setSuzgec(BOS_EGITIM_SUZGECI)}
            testId="egitim-suzgec-gostergesi"
          />
          {egitimler.isSuccess && toplam === 0 && (
            <BosListeNotu
              mesaj={
                etkin > 0
                  ? 'Bu süzgeçlerle eşleşen eğitim kaydı yok — süzgeçleri temizleyebilirsiniz.'
                  : 'Görüntüleyebileceğiniz eğitim kaydı yok.'
              }
            />
          )}
          {satirlar.length > 0 && (
            <Table striped highlightOnHover data-testid="egitim-listesi">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Tarih</Table.Th>
                  <Table.Th>Eğitim</Table.Th>
                  <Table.Th>İşyeri</Table.Th>
                  <Table.Th>Yöntem</Table.Th>
                  <Table.Th>Süre (ders saati)</Table.Th>
                  <Table.Th>Katılımcı</Table.Th>
                  <Table.Th>Değerlendirme</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {satirlar.map((kayit) => (
                  <Table.Tr
                    key={kayit.id}
                    style={{ cursor: 'pointer' }}
                    data-testid={`egitim-satir-${kayit.id}`}
                    onClick={() => void navigate(`/egitimler/${kayit.id}`)}
                  >
                    <Table.Td>{tarihGoster(kayit.tarih)}</Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Text size="sm">{kayit.egitimTuruAd}</Text>
                        {kayit.sureUyarisiVar && (
                          <Badge color={ANLAM.dikkat} size="sm" data-testid="egitim-sure-uyarisi">
                            süre uyarısı
                          </Badge>
                        )}
                        {/* SÖ-1 — sistem öncesi kayıt (ekipman kontrol geçmişi rozetinin ikizi). */}
                        {kayit.devirMi && (
                          <Rozet anlam="bilgi" testId="egitim-devir-rozeti">
                            Sistem öncesi kayıt
                          </Rozet>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {kayit.workplaceAd}
                      {kayit.locationAd ? ` — ${kayit.locationAd}` : ''}
                    </Table.Td>
                    <Table.Td>
                      {kayit.yontem === null
                        ? '—'
                        : (EGITIM_YONTEM_ETIKET[kayit.yontem] ?? kayit.yontem)}
                    </Table.Td>
                    <Table.Td>{kayit.sureDersSaati ?? '—'}</Table.Td>
                    <Table.Td>
                      {kayit.katilimciSayisi} ({kayit.basariliSayisi} başarılı)
                    </Table.Td>
                    <Table.Td data-testid={`egitim-oran-${kayit.id}`}>
                      {/* TÜRETİLMİŞ oran (T4) — "kim doldurmadı" BİLİNMEZ. */}
                      {kayit.katilimciSayisi} katılımcı, {kayit.degerlendirmeYanitSayisi} yanıt
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
          {egitimler.isSuccess && toplam > 0 && (
            <Sayfalama
              sayfa={suzgec.sayfa}
              sayfaBoyutu={EGITIM_SAYFA_BOYUTU}
              toplam={toplam}
              onDegis={(sayfa) => setSuzgec((eski) => ({ ...eski, sayfa }))}
              testId="egitim-sayfalama"
            />
          )}
        </>
      )}
    </Stack>
  );
}

/**
 * **Y4 — EĞİTİM KAYITLARININ TOPLU YÜKLENMESİ (tur 5).** Ekipman/devir
 * panelinin eğitim kolu: satır = KATILIMCI, oturum anahtarıyla gruplanır;
 * rapor satır numarası + sebep + oturum bağlantısı taşır. UYARI ≠ HATA
 * (süre uyarısı satırı yeşil değil dikkat rengiyle listelenir).
 */
function EgitimYuklemePaneli(): React.JSX.Element {
  const yukle = useEgitimYukle();
  const indir = useCiktiIndir();
  const [dosya, setDosya] = useState<File | null>(null);
  return (
    <Stack gap="sm" data-testid="egitim-yukleme-paneli">
      <Group align="flex-end">
        <Button
          variant="default"
          size="xs"
          data-testid="egitim-sablon-indir"
          loading={indir.isPending}
          onClick={() =>
            indir.mutate({
              yol: '/api/trainings/import/template',
              yedekAd: 'egitim-kayit-sablonu.xlsx',
            })
          }
        >
          Şablonu indir
        </Button>
        <FileInput
          data-testid="egitim-yukleme-dosya"
          placeholder="Doldurulmuş şablonu seçin (.xlsx / .csv)"
          accept=".xlsx,.csv"
          value={dosya}
          onChange={(secilen) => {
            setDosya(secilen);
            yukle.reset();
          }}
          clearable
          size="xs"
          w={300}
        />
        <Button
          size="xs"
          data-testid="egitim-yukle"
          disabled={!dosya}
          loading={yukle.isPending}
          onClick={() => dosya && yukle.mutate({ dosya })}
        >
          Yükle
        </Button>
      </Group>
      {yukle.isError && (
        <Alert color={ANLAM.engel} role="alert" data-testid="egitim-yukleme-hata">
          {yukle.error.message}
        </Alert>
      )}
      {yukle.data && (
        <Stack gap="xs">
          <Group gap="xs" data-testid="egitim-yukleme-ozeti">
            <Badge color={ANLAM.olumlu} variant="light">
              Eklendi: {yukle.data.eklenen}
            </Badge>
            <Badge color={ANLAM.bilgi} variant="light">
              Oturum: {yukle.data.oturum}
            </Badge>
            <Badge color={ANLAM.engel} variant="light">
              İşlenemedi: {yukle.data.hatali}
            </Badge>
          </Group>
          <YuklemeSonucTablosu rapor={yukle.data} testId="egitim-yukleme-sonuc-tablosu" />
        </Stack>
      )}
    </Stack>
  );
}
